/**
 * USD→EUR exchange rates of the catalog page, as `GET /internal/exchange-rates`
 * returns them (serialized domain `ExchangeRate`). Display helpers only: the
 * rates are never used here for a money calculation.
 */
export interface Rate {
  /** Calendar date `YYYY-MM-DD`. */
  date: string;
  /** Decimal string, point as separator. */
  usdToEur: string;
  source: string;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Window of the rate trend (stat card and chart), in days. */
export const RATE_TREND_DAYS = 14;

export function rateSourceLabel(source: string): string {
  switch (source) {
    case 'ecb':
      return 'BCE';
    case 'manual':
      return 'Saisie manuelle';
    default:
      return source;
  }
}

/** « 0,888652 » : the rate as stored, comma as decimal separator. */
export function formatRate(value: string): string {
  return value.replace('.', ',');
}

/** « 16/10/2026 » from `2026-10-16`; any other string is shown as is. */
export function formatRateDate(date: string): string {
  const match = ISO_DATE.exec(date);
  if (match === null) {
    return date;
  }
  return `${match[3] ?? ''}/${match[2] ?? ''}/${match[1] ?? ''}`;
}

/** « 16/10 » from `2026-10-16` (chart axis). */
export function formatRateDay(date: string): string {
  const match = ISO_DATE.exec(date);
  if (match === null) {
    return date;
  }
  return `${match[3] ?? ''}/${match[2] ?? ''}`;
}

/** `YYYY-MM-DD` shifted by `days` calendar days. */
export function shiftIsoDate(date: string, days: number): string {
  const match = ISO_DATE.exec(date);
  if (match === null) {
    return date;
  }
  const shifted = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + days),
  );
  return shifted.toISOString().slice(0, 10);
}

/** Most recent first; ISO dates sort lexicographically. */
export function sortRatesDesc(rates: readonly Rate[]): Rate[] {
  return [...rates].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

/**
 * The rate for `date` or the last known one before it, as the domain picks it
 * (`selectExchangeRate`, PRD 8.2): the first rate of the latest date wins.
 */
export function rateOn(rates: readonly Rate[], date: string): Rate | undefined {
  let best: Rate | undefined;
  for (const rate of rates) {
    if (rate.date <= date && (best === undefined || rate.date > best.date)) {
      best = rate;
    }
  }
  return best;
}

/** The most recent rate known, whatever its date. */
export function latestRate(rates: readonly Rate[]): Rate | undefined {
  let best: Rate | undefined;
  for (const rate of rates) {
    if (best === undefined || rate.date > best.date) {
      best = rate;
    }
  }
  return best;
}

/** « -0,25 % » : relative change from `from` to `to`, for display. */
export function formatRateChange(from: string, to: string): string | null {
  const start = Number(from);
  const end = Number(to);
  if (!Number.isFinite(start) || !Number.isFinite(end) || start <= 0) {
    return null;
  }
  const percent = (end / start - 1) * 100;
  const formatted = percent.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    signDisplay: 'exceptZero',
  });
  return `${formatted}\u00a0%`;
}

/** « 0,8887 » : a rate rounded to four decimals, for the chart axis and the trend. */
export function formatRateShort(value: number): string {
  return value.toLocaleString('fr-FR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
}
